import type {
  DocumentData,
  DocumentSnapshot,
  FirestoreDataConverter,
  PartialWithFieldValue,
  QueryDocumentSnapshot,
  QuerySnapshot,
} from 'firebase/firestore';
import type { z } from 'zod';

import { type DocMapper, identityDocMapper } from '@scaffold/common/utils';

import { NotFoundError } from './errors';

export const convert = <T>() => ({
  toFirestore: (data: PartialWithFieldValue<T>) => data,
  fromFirestore: (doc: QueryDocumentSnapshot) => doc.data() as T,
});

export function buildConverter<Z extends z.ZodType<unknown, DocumentData>>(
  schema: Z,
  mapper: DocMapper = identityDocMapper,
): FirestoreDataConverter<z.output<Z>, z.input<Z>> {
  return {
    // TODO: Use Zod converters
    toFirestore: () => undefined as z.input<Z>,
    fromFirestore: (snap: QueryDocumentSnapshot) => {
      const result = schema.safeParse(mapper(snap));
      if (!result.error) return result.data;
      if (import.meta.env.DEV) {
        console.debug(result, mapper(snap));
      }
      throw result.error;
    },
  };
}

export function buildInvertedConverter<
  Z extends z.ZodType<DocumentData, unknown>,
>(
  schema: Z,
  mapper: DocMapper = identityDocMapper,
): FirestoreDataConverter<z.input<Z>, z.output<Z>> {
  return {
    // TODO: Use Zod converters
    toFirestore: () => undefined as z.output<Z>,
    fromFirestore: (snap: QueryDocumentSnapshot) => {
      const result = schema.safeEncode(mapper(snap) as z.output<Z>);
      if (!result.error) return result.data;
      if (import.meta.env.DEV) {
        console.debug(result, mapper(snap));
      }
      throw result.error;
    },
  };
}

export const extractDocs = <O extends DocumentData, I extends DocumentData>(
  snapshot: QuerySnapshot<O, I>,
) => snapshot.docs.map((doc) => doc.data());

export const extractSafeDocs = <O extends DocumentData, I extends DocumentData>(
  snapshot: QuerySnapshot<O, I>,
) =>
  snapshot.docs.map((doc) => {
    try {
      const data = doc.data();
      return data;
    } catch (err) {
      return undefined;
    }
  });

export const extractExistingDoc = <
  O extends DocumentData,
  I extends DocumentData,
>(
  doc: DocumentSnapshot<O, I>,
) => {
  if (!doc.exists()) throw new NotFoundError(`Not found doc ${doc.id}`);

  return doc.data();
};
